import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getMinecraftResource, 
  toggleLike, 
  incrementDownloadCount,
  deleteMinecraftResource,
  updateMinecraftResource,
  uploadGalleryImage,
  uploadThumbnail
} from '../../firebase/minecraft';
import { MinecraftResource } from '../../types/minecraft';
import WorldShareTemplate, { WorldData, GalleryImage } from './templates/WorldShareTemplate';
import Button from '../../components/ui/Button';

const MinecraftEducationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [resource, setResource] = useState<MinecraftResource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedWorldData, setEditedWorldData] = useState<WorldData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingGalleryFiles, setPendingGalleryFiles] = useState<Record<string, File>>({});
  const pendingGalleryFilesRef = React.useRef<Record<string, File>>({});
  const [pendingThumbnailFile, setPendingThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(null);

  const revokePendingGalleryUrls = React.useCallback((urls: Record<string, File>) => {
    Object.keys(urls).forEach((key) => {
      URL.revokeObjectURL(key);
    });
  }, []);

  useEffect(() => {
    pendingGalleryFilesRef.current = pendingGalleryFiles;
  }, [pendingGalleryFiles]);

  useEffect(() => {
    return () => {
      revokePendingGalleryUrls(pendingGalleryFilesRef.current);
    };
  }, [revokePendingGalleryUrls]);

  useEffect(() => {
    const fetchResource = async () => {
      if (!id) {
        setError('리소스 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getMinecraftResource(id);
        if (!data) {
          setError('리소스를 찾을 수 없습니다.');
        } else {
          setResource(data);
        }
      } catch (error) {
        console.error('리소스 로드 오류:', error);
        setError('리소스를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchResource();
  }, [id]);

  const handleLike = async () => {
    if (!currentUser || !resource || isEditMode) {
      if (!currentUser) {
      alert('로그인이 필요합니다.');
      }
      return;
    }

    try {
      await toggleLike(resource.id, currentUser.uid, resource.likes, resource.likedBy);
      const isLiked = resource.likedBy.includes(currentUser.uid);
      setResource({
        ...resource,
        likes: isLiked ? resource.likes - 1 : resource.likes + 1,
        likedBy: isLiked 
          ? resource.likedBy.filter(id => id !== currentUser.uid)
          : [...resource.likedBy, currentUser.uid]
      });
    } catch (error) {
      console.error('좋아요 처리 오류:', error);
      alert('좋아요 처리 중 오류가 발생했습니다.');
    }
  };

  const handleDownload = async () => {
    if (!resource || isEditMode) return;

    try {
      window.open(resource.fileUrl, '_blank');
      await incrementDownloadCount(resource.id, resource.downloadCount);
      setResource({
        ...resource,
        downloadCount: resource.downloadCount + 1
      });
    } catch (error) {
      console.error('다운로드 오류:', error);
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!resource || !currentUser) return;

    if (resource.authorId !== currentUser.uid) {
      alert('삭제 권한이 없습니다.');
      return;
    }

    if (!window.confirm('정말로 이 리소스를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    setDeleting(true);
    try {
      await deleteMinecraftResource(resource.id, resource.fileUrl, resource.thumbnailUrl);
      alert('리소스가 삭제되었습니다.');
      navigate('/minecraft-education');
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const convertResourceToWorldData = (resource: MinecraftResource): WorldData => ({
    title: resource.title,
    subtitle: resource.subtitle || resource.description,
    tags: resource.tags || [],
    thumbnail: resource.thumbnailUrl || '/images/minecraft/world-thumbnail-placeholder.png',
    version: resource.version || 'v1.0',
    minecraftVersion: resource.minecraftVersion || 'Education Edition',
    fileSize: formatFileSize(resource.fileSize),
    downloadUrl: resource.fileUrl,
    teacherGuideUrl: resource.teacherGuideUrl,
    overview: resource.overview || resource.description,
    source: resource.source || '',
    learningObjectives: resource.learningObjectives || [],
    recommendedGrade: resource.recommendedGrade || '',
    playTime: resource.playTime || '',
    playerMode: (resource.playerMode || '싱글/멀티 지원') as '싱글 플레이' | '멀티 플레이' | '싱글/멀티 지원',
    prerequisites: resource.prerequisites || [],
    gallery: resource.gallery || [],
    installationSteps: resource.installationSteps || [],
    lessonIdeas: resource.lessonIdeas || [],
    missions: resource.missions || [],
    tips: resource.tips || [],
    resourceLinks: resource.resourceLinks || []
  });

  const convertWorldDataToResourceUpdate = (data: WorldData) => ({
    title: data.title,
    description: data.overview,
    type: 'world' as const,
    tags: data.tags,
    version: data.version,
    minecraftVersion: data.minecraftVersion,
    subtitle: data.subtitle,
    overview: data.overview,
    learningObjectives: data.learningObjectives,
    recommendedGrade: data.recommendedGrade,
    playTime: data.playTime,
    playerMode: data.playerMode,
    prerequisites: data.prerequisites,
    gallery: data.gallery,
    installationSteps: data.installationSteps,
    lessonIdeas: data.lessonIdeas,
    missions: data.missions,
    tips: data.tips,
    resourceLinks: data.resourceLinks,
    source: data.source,
    teacherGuideUrl: data.teacherGuideUrl
  });

  const handleWorldDataChange = (data: WorldData) => {
    setEditedWorldData(data);
  };

  const handleStartEdit = () => {
    if (!resource) return;
    setEditedWorldData(convertResourceToWorldData(resource));
    setIsEditMode(true);
    setSaveError(null);
    revokePendingGalleryUrls(pendingGalleryFilesRef.current);
    setPendingGalleryFiles({});
    setPendingThumbnailFile(null);
    setThumbnailPreviewUrl(null);
  };

  const handleCancelEdit = () => {
    revokePendingGalleryUrls(pendingGalleryFilesRef.current);
    setPendingGalleryFiles({});
    if (thumbnailPreviewUrl) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    setPendingThumbnailFile(null);
    setThumbnailPreviewUrl(null);
    setIsEditMode(false);
    setEditedWorldData(null);
    setSaveError(null);
  };

  const handleAddGalleryFile = (file: File) => {
    if (!isEditMode) return;
    const previewUrl = URL.createObjectURL(file);
    setPendingGalleryFiles((prev) => ({
      ...prev,
      [previewUrl]: file
    }));
    setEditedWorldData((prev) => {
      if (!prev) return prev;
      const nextGallery = [...(prev.gallery || [])];
      nextGallery.push({
        src: previewUrl,
        alt: file.name,
        description: '',
        type: 'image'
      });
      return {
        ...prev,
        gallery: nextGallery
      };
    });
  };

  const handleAddGalleryYoutube = (url: string) => {
    if (!isEditMode || !url.trim()) return;
    setEditedWorldData((prev) => {
      if (!prev) return prev;
      const nextGallery = [...(prev.gallery || [])];
      nextGallery.push({
        src: url.trim(),
        alt: 'YouTube 링크',
        description: '',
        type: 'youtube'
      });
      return {
        ...prev,
        gallery: nextGallery
      };
    });
  };

  const handleThumbnailChange = (file: File) => {
    if (!isEditMode) return;
    const previewUrl = URL.createObjectURL(file);
    if (thumbnailPreviewUrl) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    setThumbnailPreviewUrl(previewUrl);
    setPendingThumbnailFile(file);
    setEditedWorldData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        thumbnail: previewUrl
      };
    });
  };

  const handleRemoveGalleryItem = (index: number) => {
    if (!isEditMode) return;
    let removedItem: GalleryImage | undefined;
    setEditedWorldData((prev) => {
      if (!prev) return prev;
      const currentGallery = [...(prev.gallery || [])];
      if (index < 0 || index >= currentGallery.length) return prev;
      [removedItem] = currentGallery.splice(index, 1);
      return {
        ...prev,
        gallery: currentGallery
      };
    });
    if (removedItem) {
      const fileMap = pendingGalleryFilesRef.current;
      if (fileMap[removedItem.src]) {
        const srcKey = removedItem.src;
        URL.revokeObjectURL(srcKey);
        setPendingGalleryFiles((prev) => {
          const updated = { ...prev };
          delete updated[srcKey];
          return updated;
        });
      }
    }
  };

  const handleSave = async () => {
    if (!resource || !editedWorldData) return;

    setSaving(true);
    setSaveError(null);
    try {
      // 썸네일 업로드 처리
      let thumbnailUrl = resource.thumbnailUrl;
      if (pendingThumbnailFile) {
        thumbnailUrl = await uploadThumbnail(pendingThumbnailFile, resource.id);
        if (thumbnailPreviewUrl) {
          URL.revokeObjectURL(thumbnailPreviewUrl);
        }
        setThumbnailPreviewUrl(null);
        setPendingThumbnailFile(null);
      }

      const fileMap = pendingGalleryFilesRef.current;
      const galleryWithUploads = await Promise.all(
        (editedWorldData.gallery || []).map(async (item, index) => {
          const pendingFile = fileMap[item.src];
          if (item.type !== 'youtube' && pendingFile) {
            const downloadUrl = await uploadGalleryImage(pendingFile, resource.id, index);
            URL.revokeObjectURL(item.src);
            return {
              ...item,
              src: downloadUrl,
              type: 'image' as const
            };
          }
          return item;
        })
      );

      const updatePayload: any = convertWorldDataToResourceUpdate({
        ...editedWorldData,
        gallery: galleryWithUploads
      });
      if (thumbnailUrl) {
        updatePayload.thumbnailUrl = thumbnailUrl;
      }
      await updateMinecraftResource(resource.id, updatePayload, resource);
      setResource((prev) =>
        prev
          ? {
              ...prev,
              ...updatePayload,
              thumbnailUrl: thumbnailUrl ?? prev.thumbnailUrl,
              description: updatePayload.description ?? prev.description,
              tags: updatePayload.tags ?? prev.tags,
              version: updatePayload.version ?? prev.version,
              minecraftVersion: updatePayload.minecraftVersion ?? prev.minecraftVersion,
              subtitle: updatePayload.subtitle ?? prev.subtitle,
              overview: updatePayload.overview ?? prev.overview,
              learningObjectives: updatePayload.learningObjectives ?? prev.learningObjectives,
              recommendedGrade: updatePayload.recommendedGrade ?? prev.recommendedGrade,
              playTime: updatePayload.playTime ?? prev.playTime,
              playerMode: updatePayload.playerMode ?? prev.playerMode,
              prerequisites: updatePayload.prerequisites ?? prev.prerequisites,
              installationSteps: updatePayload.installationSteps ?? prev.installationSteps,
              lessonIdeas: updatePayload.lessonIdeas ?? prev.lessonIdeas,
              missions: updatePayload.missions ?? prev.missions,
              tips: updatePayload.tips ?? prev.tips,
              resourceLinks: updatePayload.resourceLinks ?? prev.resourceLinks,
              teacherGuideUrl: updatePayload.teacherGuideUrl ?? prev.teacherGuideUrl,
            }
          : prev
      );
      setIsEditMode(false);
      setEditedWorldData(null);
      revokePendingGalleryUrls(fileMap);
      setPendingGalleryFiles({});
      setPendingThumbnailFile(null);
      setThumbnailPreviewUrl(null);
    } catch (err) {
      console.error('리소스 업데이트 오류:', err);
      setSaveError('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div style={{ width: '100%', height: '100%', padding: '24px' }}>
        <div style={{
          padding: '16px 20px',
          background: '#FFF5F5',
          border: '1px solid #FFE0E0',
          borderRadius: '8px',
          color: '#FF3838',
          marginBottom: '16px'
        }}>
          {error || '리소스를 찾을 수 없습니다.'}
        </div>
        <Button
          width="auto"
          height="40px"
          variant="cancel"
          onClick={() => navigate('/minecraft-education')}
        >
          목록으로 돌아가기
        </Button>
      </div>
    );
  }

  if (resource.type !== 'world') {
    return (
      <div style={{ width: '100%', height: '100%', padding: '24px', background: '#F4F6FA' }}>
        <div style={{ marginBottom: '24px' }}>
          <Button
            width="auto"
            height="40px"
            variant="cancel"
            onClick={() => navigate('/minecraft-education')}
          >
            ← 목록으로
          </Button>
        </div>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ margin: '0 0 16px 0', fontSize: '32px', fontWeight: 600 }}>{resource.title}</h1>
          <p style={{ margin: '0 0 24px 0', color: '#4C4C4C', lineHeight: 1.6 }}>{resource.description}</p>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <Button
              width="160px"
              height="44px"
              variant="submit"
              onClick={handleDownload}
            >
              다운로드
            </Button>
            <button
              onClick={handleLike}
              style={{
                width: '44px',
                height: '44px',
                border: '1px solid #F0F0F0',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}
            >
              {resource.likedBy.includes(currentUser?.uid || '') ? '❤️' : '🤍'}
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              border: '1px solid #F0F0F0',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600
            }}>
              {resource.likes}
            </div>
          </div>
          {currentUser && resource.authorId === currentUser.uid && (
            <Button
              width="auto"
              height="40px"
              variant="cancel"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? '삭제 중...' : '삭제'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const viewWorldData = convertResourceToWorldData(resource);
  let displayWorldData = isEditMode
    ? editedWorldData ?? viewWorldData
    : viewWorldData;
  
  // 썸네일 미리보기가 있으면 사용
  if (isEditMode && thumbnailPreviewUrl && displayWorldData) {
    displayWorldData = {
      ...displayWorldData,
      thumbnail: thumbnailPreviewUrl
    };
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        // 페이지 전체 가로 스크롤 차단 (캐러셀 내부만 가로 스크롤)
        overflowX: 'hidden',
        overscrollBehaviorX: 'none',
        touchAction: 'pan-y'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: '12px',
          minHeight: '48px'
        }}
      >
        <Button
          width="auto"
          height="40px"
          variant="cancel"
          onClick={() => {
            if (isEditMode) {
              handleCancelEdit();
            } else {
              navigate('/minecraft-education');
            }
          }}
        >
          ← 목록으로
        </Button>

        <div
          style={{
                    display: 'flex',
                    alignItems: 'center',
            gap: '8px',
            flexWrap: 'nowrap'
          }}
        >
                  <Button
            width="48px"
            height="40px"
            variant={isEditMode ? 'line' : 'edit'}
            onClick={handleLike}
            disabled={isEditMode}
                  >
            {resource.likedBy.includes(currentUser?.uid || '') ? '❤️' : '🤍'}
                  </Button>
          <Button
            width="72px"
            height="40px"
            variant="line"
            disabled
          >
                      {resource.likes}
          </Button>

          {currentUser && resource.authorId === currentUser.uid && (
            isEditMode ? (
              <>
                <Button
                  width="auto"
                  height="40px"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
              취소
            </Button>
                <Button
                  width="auto"
                  height="40px"
                  variant="submit"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? '저장 중...' : '저장'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  width="auto"
                  height="40px"
                  variant="line"
                  onClick={handleStartEdit}
                >
                  수정
                </Button>
                <Button
                  width="auto"
                  height="40px"
                  variant="cancel"
                  onClick={handleDelete}
                  disabled={deleting}
                >
              {deleting ? '삭제 중...' : '삭제'}
            </Button>
              </>
            )
          )}
        </div>
      </div>

      {saveError && (
        <div
          style={{
            alignSelf: 'flex-end',
            padding: '12px 16px',
            background: '#FFF5F5',
            border: '1px solid #FFE0E0',
            borderRadius: '8px',
            color: '#FF3838'
          }}
        >
          {saveError}
        </div>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0
        }}
      >
        <WorldShareTemplate
          worldData={displayWorldData}
          onDownload={handleDownload}
          mode={isEditMode ? 'edit' : 'view'}
          onWorldDataChange={isEditMode ? handleWorldDataChange : undefined}
          onAddGalleryFile={isEditMode ? handleAddGalleryFile : undefined}
          onAddGalleryYoutube={isEditMode ? handleAddGalleryYoutube : undefined}
          onRemoveGalleryItem={isEditMode ? handleRemoveGalleryItem : undefined}
          onThumbnailChange={isEditMode ? handleThumbnailChange : undefined}
        />
      </div>
    </div>
  );
};

export default MinecraftEducationDetail;

